/**
 * Bandeau permanent: heure et date, besoins, ressources, infobulle de cible,
 * compteur d'images par seconde, messages flottants et rangee de boutons.
 *
 * La racine du HUD est transparente aux evenements: seuls les elements
 * interactifs (les boutons) recoivent les clics, pour ne pas voler les taps
 * destines au monde 3D.
 */
import '../styles/ui.css'
import type { Besoins } from '../../shared/types/player.ts'
import type { Inventaire, ItemId } from '../../shared/types/items.ts'
import type { Horloge } from '../../shared/sim/temps.ts'
import { formaterDate, formaterHeure, iconePhase, nomPhase } from '../../shared/sim/temps.ts'
import { etatBesoin } from '../../shared/sim/besoins.ts'
import { objet } from '../../shared/data/objets.ts'
import { quantite } from '../../shared/sim/inventaire.ts'
import { journal } from '../log/journal.ts'
import { clicSonore, el, refusSonore } from './commun.ts'
import { estAdminLocal } from './connexion.ts'

export type NomBouton =
  | 'construire'
  | 'inventaire'
  | 'lampe'
  | 'feux'
  | 'personnage'
  | 'reglages'
  | 'admin'
  | 'ancre'
  | 'verrou'
  | 'monde'

export type TonMessage = 'info' | 'succes' | 'erreur'

interface DefBouton {
  nom: NomBouton
  icone: string
  texte: string
}

const DEFS_BOUTONS: readonly DefBouton[] = [
  // L'ancre n'apparait qu'a bord d'une barque a flot. Elle est ici parce que,
  // au doigt, elle n'existait nulle part: jeter l'ancre n'etait qu'une touche
  // « a », donc pecher depuis la barque etait impossible sur un telephone.
  { nom: 'ancre', icone: '⚓', texte: 'Ancre' },
  // Le verrou suit la meme regle que l'ancre: il n'apparait qu'a bord de SON
  // PROPRE vehicule. Un cadenas toujours visible serait une enigme pour qui
  // marche a pied, et un cadenas sur la voiture d'un autre une promesse fausse.
  { nom: 'verrou', icone: '🔓', texte: 'Verrou' },
  // Le voyage entre les mondes. Meme regle que l'ancre et le verrou: il
  // n'apparait qu'aux commandes d'un appareil GARE a un aeroport, parce que
  // c'est le seul endroit d'ou il veut dire quelque chose. En vol, on pilote en
  // designant un point; au sol et a pied, il n'y a pas d'avion pour partir.
  { nom: 'monde', icone: '🌍', texte: 'Autre monde' },
  { nom: 'construire', icone: '🔨', texte: 'Construire' },
  { nom: 'inventaire', icone: '🎒', texte: 'Inventaire' },
  { nom: 'lampe', icone: '🔦', texte: 'Lampe' },
  { nom: 'feux', icone: '🎆', texte: 'Feux' },
  { nom: 'personnage', icone: '🧑', texte: 'Personnage' },
  { nom: 'reglages', icone: '⚙️', texte: 'Réglages' },
  { nom: 'admin', icone: '🛠️', texte: 'Admin' },
]

/** Ressources affichees en permanence dans la barre compacte. */
const RESSOURCES_BARRE: readonly ItemId[] = ['bois', 'pierre', 'fibre', 'sable', 'fusee']

/** Nombre maximum de messages empiles a l'ecran. */
const MESSAGES_MAX = 4

/** Duree par defaut d'un message, en ms. */
const DUREE_MESSAGE = 2600

interface Jauge {
  racine: HTMLDivElement
  remplissage: HTMLDivElement
  valeur: HTMLElement
  etat: string
}

export class Hud {
  /** Derniere reference d'inventaire affichee, pour eviter le travail inutile. */
  private dernierInventaire: Inventaire | null = null

  /** Rangee de boutons d'action, en bas de l'ecran. */
  readonly boutons: HTMLDivElement
  /** Appele au clic sur un bouton d'action. */
  onBouton: ((nom: NomBouton) => void) | null = null
  /** Toucher la pastille d'infobulle quand elle nomme une cible precise. */
  onCible: (() => void) | null = null

  private readonly racine: HTMLDivElement
  private readonly icone: HTMLElement
  private readonly heure: HTMLElement
  private readonly date: HTMLElement
  private readonly carteTemps: HTMLDivElement
  private readonly jaugeFaim: Jauge
  private readonly jaugeEnergie: Jauge
  private readonly barreRessources: HTMLDivElement
  private readonly prises: HTMLDivElement
  private readonly cible: HTMLDivElement
  private readonly compteurFps: HTMLDivElement
  private readonly zoneMessages: HTMLDivElement
  private readonly puces = new Map<ItemId, { racine: HTMLElement; nombre: HTMLElement; n: number }>()
  private readonly parBouton = new Map<NomBouton, HTMLButtonElement>()

  private dernierTemps = ''
  private dernierInv: Inventaire | null = null
  private dernierFps = -1
  private feuxPossible = true
  private raisonFeux = ''
  private minuteries: number[] = []

  constructor(parent: HTMLElement) {
    this.racine = el('div', 'hud')

    // -------------------------------------------------------------- haut
    const haut = el('div', 'hud__haut')

    this.carteTemps = el('div', 'temps')
    this.icone = el('span', 'temps__icone', '🌞')
    const textes = el('div', 'temps__textes')
    this.heure = el('span', 'temps__heure', '--:--')
    this.date = el('span', 'temps__date', 'Jour 1')
    textes.append(this.heure, this.date)
    this.carteTemps.append(this.icone, textes)

    const besoins = el('div', 'hud__besoins')
    this.jaugeFaim = this.creerJauge('🍗', 'Faim')
    this.jaugeEnergie = this.creerJauge('⚡', 'Énergie')
    besoins.append(this.jaugeFaim.racine, this.jaugeEnergie.racine)

    const droite = el('div', 'hud__droite')
    this.barreRessources = el('div', 'ressources')
    for (const id of RESSOURCES_BARRE) this.barreRessources.append(this.creerPuce(id))
    this.compteurFps = el('div', 'fps', '')
    this.compteurFps.hidden = true
    this.prises = el('div', 'prises')
    droite.append(this.barreRessources, this.compteurFps)

    haut.append(this.carteTemps, besoins, droite)

    // ---------------------------------------------------------- messages
    this.zoneMessages = el('div', 'hud__messages')

    // ------------------------------------------------------------- bas
    const bas = el('div', 'hud__bas')
    this.cible = el('div', 'cible')
    this.cible.hidden = true
    this.cible.addEventListener('click', (e) => {
      if (!this.cible.classList.contains('cible--action')) return
      e.stopPropagation()
      this.onCible?.()
    })
    this.boutons = el('div', 'hud__boutons')
    for (const def of DEFS_BOUTONS) {
      // Le bouton d'administration n'existe que pour un compte admin. C'est de
      // l'affichage: le droit reel est revérifie par le serveur a chaque requete.
      if (def.nom === 'admin' && !estAdminLocal()) continue
      this.boutons.append(this.creerBouton(def))
    }
    // Hors d'une barque, l'ancre n'a aucun sens: elle reste cachee jusqu'a ce
    // qu'on soit a bord, sur l'eau.
    this.majAncre(false, false)
    // Meme raison: on n'est pas encore monte, il n'y a rien a verrouiller.
    this.majVerrou(false, false)
    this.majMonde(false, 2)
    bas.append(this.cible, this.boutons)
    // La hauteur REELLE de la rangee de boutons, pour ce qui doit se poser
    // au-dessus (le chat). Elle se replie sur deux ou trois lignes selon la
    // largeur et les boutons visibles: un retrait ecrit en pixels laissait la
    // bulle du chat sur « Construire » des que la troisieme ligne apparaissait.
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(() => {
        document.documentElement.style.setProperty('--hud-boutons-h', `${this.boutons.offsetHeight}px`)
      }).observe(this.boutons)
    }

    this.racine.append(haut, this.prises, this.zoneMessages, bas)
    parent.append(this.racine)
    journal.info('menu', 'HUD en place')
  }

  // ------------------------------------------------------------- temps

  majTemps(h: Horloge): void {
    const cle = `${h.phase}|${formaterHeure(h)}|${h.jour}`
    if (cle === this.dernierTemps) return
    this.dernierTemps = cle
    this.icone.textContent = iconePhase(h.phase)
    this.heure.textContent = formaterHeure(h)
    this.date.textContent = formaterDate(h)
    this.carteTemps.dataset['phase'] = h.phase
    this.carteTemps.title = `${nomPhase(h.phase)} — ${formaterHeure(h)}, ${formaterDate(h)}`
  }

  // ------------------------------------------------------------ besoins

  majBesoins(b: Besoins): void {
    this.majJauge(this.jaugeFaim, b.faim)
    this.majJauge(this.jaugeEnergie, b.energie)
  }

  private creerJauge(icone: string, nom: string): Jauge {
    const racine = el('div', 'besoin')
    racine.title = nom
    racine.append(el('span', 'besoin__icone', icone))
    const piste = el('div', 'besoin__piste')
    const remplissage = el('div', 'besoin__remplissage')
    piste.append(remplissage)
    const valeur = el('span', 'besoin__valeur', '100')
    racine.append(piste, valeur)
    return { racine, remplissage, valeur, etat: '' }
  }

  private majJauge(j: Jauge, v: number): void {
    const n = Math.max(0, Math.min(100, Math.round(v)))
    j.remplissage.style.width = `${n}%`
    j.valeur.textContent = String(n)
    const etat = etatBesoin(v)
    if (etat !== j.etat) {
      j.etat = etat
      j.racine.dataset['etat'] = etat
    }
  }

  // --------------------------------------------------------- inventaire

  majInventaire(inv: Inventaire): void {
    // L'inventaire est immuable: si la reference n'a pas change, rien n'a
    // change. Sans ce garde, on le clonait et on bouclait sur ses clefs 60 fois
    // par seconde pour redessiner exactement la meme chose.
    if (inv === this.dernierInventaire) return
    this.dernierInventaire = inv

    for (const id of RESSOURCES_BARRE) {
      const puce = this.puces.get(id)
      if (!puce) continue
      const n = quantite(inv, id)
      if (n !== puce.n) {
        puce.n = n
        puce.nombre.textContent = String(n)
        puce.racine.classList.toggle('ressource--vide', n === 0)
        puce.racine.classList.remove('ressource--saut')
        // Relancer l'animation de saut sans attendre la fin de la precedente.
        void puce.racine.offsetWidth
        puce.racine.classList.add('ressource--saut')
      }
    }
    if (this.dernierInv) this.montrerPrises(this.dernierInv, inv)
    this.dernierInv = { ...inv }
  }

  private creerPuce(id: ItemId): HTMLElement {
    const def = objet(id)
    const racine = el('div', 'ressource ressource--vide')
    racine.title = def.nom
    racine.append(el('span', 'ressource__icone', def.icone))
    const nombre = el('span', 'ressource__nombre', '0')
    racine.append(nombre)
    this.puces.set(id, { racine, nombre, n: 0 })
    return racine
  }

  /** Affiche un `+N` flottant pour chaque objet dont la quantite a monte. */
  private montrerPrises(avant: Inventaire, apres: Inventaire): void {
    for (const cle of Object.keys(apres) as ItemId[]) {
      const delta = quantite(apres, cle) - quantite(avant, cle)
      if (delta <= 0) continue
      const def = objet(cle)
      const bulle = el('div', 'prise')
      bulle.append(el('span', 'prise__icone', def.icone))
      bulle.append(el('span', 'prise__texte', `+${delta}`))
      this.prises.append(bulle)
      this.plusTard(() => bulle.remove(), 1400)
    }
  }

  // -------------------------------------------------------------- cible

  majCible(texte: string | null, actionnable = false): void {
    this.cible.classList.toggle('cible--action', actionnable && !!texte)
    if (!texte) {
      if (!this.cible.hidden) {
        this.cible.hidden = true
        this.cible.textContent = ''
      }
      return
    }
    if (this.cible.textContent !== texte) this.cible.textContent = texte
    if (this.cible.hidden) {
      this.cible.hidden = false
      this.cible.classList.remove('cible--entre')
      void this.cible.offsetWidth
      this.cible.classList.add('cible--entre')
    }
  }

  // ---------------------------------------------------------------- fps

  majFps(fps: number): void {
    const n = Math.round(fps)
    if (n === this.dernierFps) return
    this.dernierFps = n
    this.compteurFps.textContent = `${n} ips`
    this.compteurFps.classList.toggle('fps--bas', n < 45)
  }

  /** Montre ou cache le compteur d'images par seconde. */
  montrerFps(v: boolean): void {
    this.compteurFps.hidden = !v
  }

  // ------------------------------------------------------------ boutons

  /**
   * Montre ou cache l'ancre, et dit ce qu'elle fera.
   *
   * Le libelle suit l'etat: on ne demande pas au joueur de deviner si le bouton
   * jette l'ancre ou la remonte. Meme regle que l'infobulle — ce qui est ecrit
   * est ce qui va se passer.
   */
  majAncre(visible: boolean, ancre: boolean): void {
    const b = this.parBouton.get('ancre')
    if (!b) return
    if (b.hidden !== !visible) b.hidden = !visible
    if (!visible) return
    const texte = ancre ? 'Remonter l’ancre' : 'Jeter l’ancre'
    const icone = ancre ? '⚓' : '🪝'
    const spanTexte = b.querySelector('.action__texte')
    const spanIcone = b.querySelector('.action__icone')
    if (spanTexte && spanTexte.textContent !== texte) spanTexte.textContent = texte
    if (spanIcone && spanIcone.textContent !== icone) spanIcone.textContent = icone
    if (b.title !== texte) {
      b.title = texte
      b.setAttribute('aria-label', texte)
    }
  }

  /**
   * Montre ou cache le verrou, et dit ce qu'il fera.
   *
   * Meme contrat que `majAncre`: le libelle annonce le GESTE, pas l'etat. « Ce
   * qui est ecrit est ce qui va se passer » est la regle de toutes les cibles
   * du jeu, et un bouton qui afficherait « Verrouillé » sans dire s'il ferme ou
   * ouvre serait exactement le mensonge que l'infobulle evite ailleurs.
   */
  majVerrou(visible: boolean, verrouille: boolean): void {
    const b = this.parBouton.get('verrou')
    if (!b) return
    if (b.hidden !== !visible) b.hidden = !visible
    if (!visible) return
    const texte = verrouille ? 'Déverrouiller' : 'Verrouiller'
    const icone = verrouille ? '🔒' : '🔓'
    const spanTexte = b.querySelector('.action__texte')
    const spanIcone = b.querySelector('.action__icone')
    if (spanTexte && spanTexte.textContent !== texte) spanTexte.textContent = texte
    if (spanIcone && spanIcone.textContent !== icone) spanIcone.textContent = icone
    if (b.title !== texte) {
      b.title = texte
      b.setAttribute('aria-label', texte)
    }
  }

  /**
   * Montre ou cache le depart vers l'autre monde, et dit OU l'on va.
   *
   * Meme contrat que `majAncre` et `majVerrou`: le libelle annonce le GESTE.
   * « Décoller pour le monde 2 » dit a la fois ce qui va se passer (on decolle)
   * et ou l'on arrive, ce qui est tout ce que le pilote a besoin de savoir —
   * l'appareil choisit ensuite l'aeroport le plus proche tout seul.
   *
   * Il reste CACHE quand l'autre monde n'a aucun aeroport: un bouton qui ne
   * peut pas aboutir est un geste sans effet, et le projet en a deja paye deux.
   */
  majMonde(visible: boolean, destination: 1 | 2): void {
    const b = this.parBouton.get('monde')
    if (!b) return
    if (b.hidden !== !visible) b.hidden = !visible
    if (!visible) return
    const texte = `Décoller pour le monde ${destination}`
    const spanTexte = b.querySelector('.action__texte')
    if (spanTexte && spanTexte.textContent !== texte) spanTexte.textContent = texte
    if (b.title !== texte) {
      b.title = texte
      b.setAttribute('aria-label', texte)
    }
  }

  private creerBouton(def: DefBouton): HTMLButtonElement {
    const b = el('button', 'action')
    b.type = 'button'
    b.title = def.texte
    b.setAttribute('aria-label', def.texte)
    b.append(el('span', 'action__icone', def.icone))
    b.append(el('span', 'action__texte', def.texte))
    b.addEventListener('click', () => {
      if (def.nom === 'feux' && !this.feuxPossible) {
        refusSonore()
        journal.input('menu', `bouton feux refuse: ${this.raisonFeux}`)
        this.message(this.raisonFeux, 3200, 'erreur')
        return
      }
      clicSonore()
      journal.input('menu', `bouton ${def.nom}`)
      this.onBouton?.(def.nom)
    })
    this.parBouton.set(def.nom, b)
    return b
  }

  /**
   * Grise le bouton des feux quand ils sont interdits. Le bouton reste
   * cliquable expres: on veut pouvoir afficher la raison au doigt.
   */
  activerFeux(possible: boolean, raison: string): void {
    this.feuxPossible = possible
    this.raisonFeux = raison
    const b = this.parBouton.get('feux')
    if (!b) return
    b.classList.toggle('action--bloque', !possible)
    b.setAttribute('aria-disabled', possible ? 'false' : 'true')
    b.title = possible ? 'Feux d’artifice' : raison
  }

  /** Marque un bouton comme actif (son panneau est ouvert). */
  marquerActif(nom: NomBouton | null): void {
    for (const [cle, b] of this.parBouton) b.classList.toggle('action--actif', cle === nom)
  }

  // ----------------------------------------------------------- messages

  message(texte: string, duree = DUREE_MESSAGE, ton: TonMessage = 'info'): void {
    while (this.zoneMessages.childElementCount >= MESSAGES_MAX) {
      this.zoneMessages.firstElementChild?.remove()
    }
    const m = el('div', `message message--${ton}`)
    const icone = ton === 'succes' ? '✅' : ton === 'erreur' ? '⚠️' : 'ℹ️'
    m.append(el('span', 'message__icone', icone))
    m.append(el('span', 'message__texte', texte))
    this.zoneMessages.append(m)
    journal.input('menu', `message (${ton}) ${texte}`)
    this.plusTard(() => {
      m.classList.add('message--sort')
      this.plusTard(() => m.remove(), 220)
    }, Math.max(600, duree))
  }

  // -------------------------------------------------------------- divers

  montrer(v: boolean): void {
    this.racine.classList.toggle('hud--cache', !v)
  }

  detruire(): void {
    for (const m of this.minuteries) window.clearTimeout(m)
    this.minuteries = []
    this.puces.clear()
    this.parBouton.clear()
    this.onBouton = null
    this.racine.remove()
  }

  private plusTard(f: () => void, ms: number): void {
    const id = window.setTimeout(() => {
      this.minuteries = this.minuteries.filter((x) => x !== id)
      f()
    }, ms)
    this.minuteries.push(id)
  }
}
